import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { LocalidadDto } from '@models/localidad-provincia/localidadDto.model';
import { LocalidadBasicDto } from '@models/localidad-provincia/localidadBasicDto.model';
import { ProvinciaDto } from '@models/localidad-provincia/provinciaDto.model';
import { LocalidadesService } from '@services/localidades/localidades.service';
import { ProvinciasService } from '@services/provincias/provincias.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';

/**
 * Buscador de provincia + localidad.
 *
 * Acepta dos formas de recibir el valor inicial porque las pantallas que lo usan
 * tienen DTOs distintos:
 *   - [localidad]        LocalidadDto      (personas)
 *   - [localidadBasicDto] LocalidadBasicDto (propiedades, empresa)
 *
 * Emite SIEMPRE un LocalidadDto por eventoLocalidad. Cuando no hay una localidad
 * válida elegida, emite una con id 0 —no null— porque hay pantallas que leen
 * event.id directo y un null las rompería.
 */
@Component({
  selector: 'app-buscar-localidad',
  templateUrl: './buscar-localidad.component.html',
  styleUrls: ['./buscar-localidad.component.css']
})
export class BuscarLocalidadComponent implements OnInit, OnChanges {

  @Input() localidad: LocalidadDto = new LocalidadDto;
  @Input() localidadBasicDto: LocalidadBasicDto = new LocalidadBasicDto;
  @Output() eventoLocalidad = new EventEmitter<LocalidadDto>();

  localidades: LocalidadDto[] = []
  lstProvinciaDto: ProvinciaDto[] = []

  formBuilder: FormBuilder = new FormBuilder;
  localidadForm: FormGroup;

  filteredLocalidades: Observable<LocalidadDto[]> = of([])
  filteredProvincia: Observable<ProvinciaDto[]> = of([])

  /** Lo último confirmado por el usuario. Es lo que se emite. */
  private localidadElegida: LocalidadDto = new LocalidadDto

  /**
   * Evita rehacer la precarga con el mismo dato. El padre puede reasignar el
   * objeto varias veces (cada GET devuelve una instancia nueva) y sin esto se
   * dispararían las mismas consultas una y otra vez.
   */
  private ultimaPrecarga: string = ''

  private provinciasCargadas = false

  get f() { return this.localidadForm.controls; }

  constructor(
    private localidadService: LocalidadesService,
    private provinciasService: ProvinciasService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService
  ) {
    this.localidadForm = this.formBuilder.group({
      Provincia: ['', Validators.compose([Validators.required])],
      Localidad: ['', Validators.compose([Validators.required])],
    });
  }

  ngOnInit(): void {
    // Sin provincia no se puede elegir localidad
    this.localidadForm.controls["Localidad"].disable()

    this.armarFiltros()

    // Si el valor inicial ya vino, ngOnChanges lo resolvió antes que esto
    if (!this.provinciasCargadas)
      this.getAllProvincias()
  }

  /**
   * El valor inicial casi nunca está en el primer render: el padre hace su GET y
   * recién después llega. Antes sólo se miraba 'localidad', así que las pantallas
   * que pasan 'localidadBasicDto' —propiedades, empresa— nunca se precargaban y
   * los dos campos quedaban vacíos aunque el registro tuviera localidad.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['localidad'] && !changes['localidadBasicDto'])
      return

    const idLocalidad = this.resolverIdLocalidad()
    const idProvincia = this.resolverIdProvincia()

    if (!idLocalidad || !idProvincia)
      return

    const clave = idProvincia + '-' + idLocalidad

    if (clave === this.ultimaPrecarga)
      return

    this.ultimaPrecarga = clave

    this.precargar(idProvincia, idLocalidad)
  }

  private resolverIdLocalidad(): number {
    if (this.localidadBasicDto && this.localidadBasicDto.id > 0)
      return this.localidadBasicDto.id

    if (this.localidad && this.localidad.id > 0)
      return this.localidad.id

    return 0
  }

  private resolverIdProvincia(): number {
    // El BasicDto trae la provincia colgando derecho; el LocalidadDto la trae
    // dentro del departamento. Se contemplan los dos y se toleran los nulos.
    if (this.localidadBasicDto && this.localidadBasicDto.provincia && this.localidadBasicDto.provincia.id > 0)
      return this.localidadBasicDto.provincia.id

    const provinciaDelDepartamento = this.localidad
      && this.localidad.departamento
      && this.localidad.departamento.provincia

    return provinciaDelDepartamento && provinciaDelDepartamento.id > 0
      ? provinciaDelDepartamento.id
      : 0
  }

  /** Trae provincias y localidades y deja los dos campos escritos */
  private precargar(idProvincia: number, idLocalidad: number) {
    this.spinner.show("spProvincia")

    this.provinciasService.getAllProvincias()
      .subscribe(
        data => {
          this.spinner.hide("spProvincia")
          this.lstProvinciaDto = data
          this.provinciasCargadas = true

          const provincia = data.find(f => f.id == idProvincia)

          // find puede no encontrar nada. El código anterior hacía un cast y
          // leía .descripcion directo: si la provincia no estaba en la lista,
          // reventaba con "cannot read property of undefined" y el campo
          // quedaba vacío sin ningún aviso.
          if (provincia)
            this.localidadForm.controls["Provincia"].setValue(provincia.descripcion)

          this.armarFiltros()

          this.cargarLocalidades(idProvincia, idLocalidad)
        },
        error => {
          this.spinner.hide("spProvincia")
          this.snackBar.showError(error, "Error");
        });
  }

  private cargarLocalidades(idProvincia: number, idLocalidad: number) {
    this.spinner.show("spLocalidades")

    this.localidadService.getLocalidadesByProvincia(idProvincia)
      .subscribe(
        data => {
          this.spinner.hide("spLocalidades")
          this.localidades = data
          this.localidadForm.controls["Localidad"].enable()

          const localidad = data.find(f => f.id == idLocalidad)

          if (localidad) {
            this.localidadForm.controls["Localidad"].setValue(localidad.descripcion)

            // Queda como elegida: si el usuario guarda sin tocar nada, se manda
            // la que ya tenía y no un id en cero.
            this.localidadElegida = localidad
            this.eventoLocalidad.emit(localidad)
          }

          this.armarFiltros()
        },
        error => {
          this.spinner.hide("spLocalidades")
          this.snackBar.showError(error, "Error");
        });
  }

  getAllProvincias() {
    this.spinner.show("spProvincia")

    this.provinciasService.getAllProvincias()
      .subscribe(
        data => {
          this.spinner.hide("spProvincia")
          this.lstProvinciaDto = data
          this.provinciasCargadas = true
          this.armarFiltros()
        },
        error => {
          this.spinner.hide("spProvincia")
          this.snackBar.showError(error, "Error");
        });
  }

  /** Elegir provincia invalida la localidad que hubiera antes */
  seleccionarProvincia(provincia: ProvinciaDto) {
    this.localidadForm.controls["Localidad"].setValue("")
    this.localidadForm.controls["Localidad"].disable()

    this.localidades = []
    this.avisarSinLocalidad()

    this.getLocalidadesByProvincia(provincia.id)
  }

  getLocalidadesByProvincia(idProvincia: number) {
    this.spinner.show("spLocalidades")

    this.localidadService.getLocalidadesByProvincia(idProvincia)
      .subscribe(
        data => {
          this.spinner.hide("spLocalidades")
          this.localidades = data
          this.localidadForm.controls["Localidad"].enable()
          this.armarFiltros()
        },
        error => {
          this.spinner.hide("spLocalidades")
          this.snackBar.showError(error, "Error");
        });
  }

  seleccionarLocalidad(localidad: LocalidadDto) {
    this.localidadElegida = localidad
    this.eventoLocalidad.emit(localidad)
  }

  /**
   * Se puede escribir cualquier cosa en el autocompletar. Si al salir del campo
   * el texto no coincide exactamente con una localidad de la lista, no hay
   * selección válida: se avisa al padre para que no guarde el id anterior.
   */
  validarTextoLocalidad() {
    const texto = (this.localidadForm.controls["Localidad"].value || '').toString().trim()

    const localidad = this.localidades.find(f => f.descripcion
      && f.descripcion.toUpperCase() === texto.toUpperCase())

    if (localidad)
      this.seleccionarLocalidad(localidad)
    else
      this.avisarSinLocalidad()
  }

  private avisarSinLocalidad() {
    this.localidadElegida = new LocalidadDto

    // id 0 y no null: hay pantallas que leen event.id sin chequear
    this.eventoLocalidad.emit(this.localidadElegida)
  }

  // ---------- Filtrado del autocompletar ----------

  private armarFiltros() {
    this.filtrarLocalidades()
    this.filtrarProvincias()
  }

  private filtrarLocalidades() {
    this.filteredLocalidades = this.localidadForm.controls["Localidad"].valueChanges.pipe(
      startWith(this.localidadForm.controls["Localidad"].value || ''),
      map((termino: any) => this.buscarLocalidad(termino))
    );
  }

  private filtrarProvincias() {
    this.filteredProvincia = this.localidadForm.controls["Provincia"].valueChanges.pipe(
      startWith(this.localidadForm.controls["Provincia"].value || ''),
      map((termino: any) => this.buscarProvincia(termino))
    );
  }

  private buscarLocalidad(valor: any): LocalidadDto[] {
    const texto = this.aTextoDeBusqueda(valor)

    if (!texto)
      return this.localidades

    return this.localidades.filter(f => f.descripcion
      && f.descripcion.toUpperCase().indexOf(texto) >= 0)
  }

  private buscarProvincia(valor: any): ProvinciaDto[] {
    const texto = this.aTextoDeBusqueda(valor)

    if (!texto)
      return this.lstProvinciaDto

    return this.lstProvinciaDto.filter(f => f.descripcion
      && f.descripcion.toUpperCase().indexOf(texto) >= 0)
  }

  /**
   * Antes el filtro armaba new RegExp(termino, 'gi') con lo que tipeaba el
   * usuario: escribir un paréntesis o un corchete tiraba "Invalid regular
   * expression" y el autocompletar dejaba de funcionar. Un indexOf alcanza y no
   * puede romperse.
   */
  private aTextoDeBusqueda(valor: any): string {
    if (valor === null || valor === undefined)
      return ''

    return valor.toString().trim().toUpperCase()
  }

  trackByItems(index: number, item: any): any { return item; }
}
